import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Button from '../../components/ui/Button.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input, Select } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { useCountries } from '../../lib/useCountries.js'

/*
 * Company profile + logo — inventory §5 General tab. Name & VAT are read-only
 * (changed via admin onboarding). Logo: base64 upload (png/jpg ≤5MB) / delete.
 * Peppol config stays server-side (intentionally not surfaced, per legacy UX).
 */
const LANGS = ['fr', 'en', 'nl']
const MAX_LOGO = 5 * 1024 * 1024

export default function CompanyTab() {
  const { t } = useTranslation()
  const { currentCompanyId } = useCompany()
  const { confirm, toast, notify } = useFeedback()
  const countries = useCountries()
  const fileRef = useRef(null)

  const [company, setCompany] = useState(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const load = () => api.get(`/companies/${currentCompanyId}`).then(({ data }) => setCompany(data?.company || data))
  useEffect(() => { if (currentCompanyId) load() }, [currentCompanyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => { setForm({ ...company }); setEditing(true) }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    try {
      await api.put(`/companies/${currentCompanyId}`, form)
      toast(t('settings.company.saved'), 'success')
      setEditing(false); load()
    } catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) }
    finally { setSaving(false) }
  }

  const onFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!/image\/(png|jpe?g)/.test(file.type)) { notify({ tone: 'error', title: t('settings.error'), message: t('settings.company.logoFormat') }); return }
    if (file.size > MAX_LOGO) { notify({ tone: 'error', title: t('settings.error'), message: t('settings.company.logoSize') }); return }
    const reader = new FileReader()
    reader.onload = async () => {
      setUploading(true)
      try { await api.post(`/companies/${currentCompanyId}/logo`, { logo: reader.result }); toast(t('settings.company.logoUpdated'), 'success'); load() }
      catch { toast(t('settings.errorGeneric'), 'error') } finally { setUploading(false); if (fileRef.current) fileRef.current.value = '' }
    }
    reader.readAsDataURL(file)
  }

  const deleteLogo = async () => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('settings.company.confirmDeleteLogo'), confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/companies/${currentCompanyId}/logo`); toast(t('settings.company.logoDeleted'), 'success'); load() }
    catch { toast(t('settings.errorGeneric'), 'error') }
  }

  if (!company) return <Card>{t('common.loading')}</Card>

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18, alignItems: 'start' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16 }}>{t('settings.company.title')}</h3>
          {!editing
            ? <Button variant="secondary" icon="pencil" onClick={startEdit}>{t('common.edit')}</Button>
            : <div style={{ display: 'flex', gap: 8 }}><Button variant="secondary" onClick={() => setEditing(false)}>{t('common.cancel')}</Button><Button variant="primary" icon="check" loading={saving} onClick={save}>{t('common.save')}</Button></div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
          <RO label={t('settings.company.name')} value={company.name} badge={company.non_vat_liable ? t('parties.fields.nonVatLiable') : null} />
          <RO label={company.non_vat_liable ? t('parties.fields.companyNumber') : t('parties.fields.vatNumber')} value={company.vat_number} />
          {editing ? (
            <>
              <Field label={t('parties.fields.contactFirstName')}><Input value={form.contact_first_name || ''} onChange={(e) => set('contact_first_name', e.target.value)} /></Field>
              <Field label={t('parties.fields.contactLastName')}><Input value={form.contact_last_name || ''} onChange={(e) => set('contact_last_name', e.target.value)} /></Field>
              <Field label={t('parties.fields.email')}><Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
              <Field label={t('parties.fields.phone')}><Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Field>
              <Field label={t('settings.company.website')}><Input value={form.website || ''} onChange={(e) => set('website', e.target.value)} /></Field>
              <Field label={t('parties.fields.country')}><Select value={form.country || 'BE'} onChange={(e) => set('country', e.target.value)}>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</Select></Field>
              <Field label={t('parties.fields.address')}><Input value={form.address || ''} onChange={(e) => set('address', e.target.value)} placeholder={t('settings.company.addressLine1')} /></Field>
              <Field label={t('settings.company.addressLine2')}><Input value={form.address_line2 || ''} onChange={(e) => set('address_line2', e.target.value)} /></Field>
              <Field label={t('settings.company.addressLine3')}><Input value={form.address_line3 || ''} onChange={(e) => set('address_line3', e.target.value)} /></Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <Field label={t('parties.fields.postalCode')}><Input value={form.postal_code || ''} onChange={(e) => set('postal_code', e.target.value)} /></Field>
                <Field label={t('parties.fields.city')}><Input value={form.city || ''} onChange={(e) => set('city', e.target.value)} /></Field>
              </div>
              <Field label={t('parties.fields.language')}>
                <div style={{ display: 'flex', gap: 8 }}>{LANGS.map((l) => <LangBtn key={l} l={l} active={(form.preferred_language || 'fr') === l} onClick={() => set('preferred_language', l)} />)}</div>
              </Field>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, alignSelf: 'center' }}>
                <input type="checkbox" checked={!!form.non_vat_liable} onChange={(e) => set('non_vat_liable', e.target.checked)} /> {t('parties.fields.nonVatLiable')}
              </label>
            </>
          ) : (
            <>
              {company.email && <RO label={t('parties.fields.email')} value={company.email} />}
              {company.phone && <RO label={t('parties.fields.phone')} value={company.phone} />}
              {company.website && <RO label={t('settings.company.website')} value={<a href={company.website} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>{company.website}</a>} />}
              {[company.contact_first_name, company.contact_last_name].filter(Boolean).length > 0 && <RO label={t('parties.fields.contact')} value={[company.contact_first_name, company.contact_last_name].filter(Boolean).join(' ')} />}
              <RO label={t('parties.fields.address')} value={<>{[company.address, company.address_line2, company.address_line3].filter(Boolean).map((l, i) => <span key={i}>{l}<br /></span>)}{company.postal_code} {company.city} {company.country ? `· ${company.country}` : ''}</>} />
            </>
          )}
        </div>
      </Card>

      <Card style={{ textAlign: 'center' }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, marginBottom: 16, textAlign: 'left' }}>{t('settings.company.logo')}</h3>
        <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-lilac)', borderRadius: 12, marginBottom: 14 }}>
          {company.logo_url
            ? <img src={company.logo_url} alt={t('settings.company.logo')} style={{ maxHeight: 96, maxWidth: '90%', objectFit: 'contain' }} />
            : <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('settings.company.noLogo')}</span>}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg" onChange={onFile} style={{ display: 'none' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button variant="secondary" icon="download" loading={uploading} onClick={() => fileRef.current?.click()}>{t('settings.company.changeLogo')}</Button>
          {company.logo_url && <Button variant="danger" icon="trash" onClick={deleteLogo}>{t('common.delete')}</Button>}
        </div>
      </Card>
    </div>
  )
}

function RO({ label, value, badge }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--color-ink)', display: 'flex', alignItems: 'center', gap: 8 }}>{value || '—'}{badge && <Badge tone="warning">{badge}</Badge>}</div>
    </div>
  )
}
function LangBtn({ l, active, onClick }) {
  return <button type="button" onClick={onClick} style={{ flex: 1, height: 40, borderRadius: 'var(--radius-field)', fontSize: 13, fontWeight: 600, border: '1.5px solid ' + (active ? 'var(--color-primary)' : 'var(--color-border-strong)'), background: active ? 'var(--color-bg-lilac-strong)' : '#fff', color: active ? 'var(--color-primary)' : 'var(--color-text)' }}>{l.toUpperCase()}</button>
}
